import * as React from 'react';
import { DataLayer } from '@grandvisionhq/analytics';
import { useIntl } from '@grandvisionhq/state';
import { useTrackPageView } from '@grandvisionhq/svc-tealium/events';
import { bookAppointmentWhatStepScreenEvent } from '../../data-layer/actions.js';
import { useAppointmentContext } from '../../pages/BookAppointment/context/AppointmentContext.js';
import { useRoutingContext } from '../../pages/BookAppointment/context/RoutingContext/context.js';
import '@swc/helpers';
import '@grandvisionhq/graphql';
import '@grandvisionhq/utils';
import '../../pages/BookAppointment/queries/get-book-appointment-store.js';
import useBookAppointmentStoreById from '../../pages/BookAppointment/hooks/api/use-book-appointment-store-by-id.js';
import '../../pages/BookAppointment/queries/get-book-appointment-type.js';
import '../../pages/BookAppointment/queries/get-book-appointment-store-by-id.js';
import { useAppointmentTypes } from '../../pages/BookAppointment/hooks/use-appointment-types.js';
import { fromArrayToString } from '../../utils/from-array-to-string.js';
import AppointmentTypes from './AppointmentTypes.js';
import useGetAgeGroupsSelectorAndSelectedAgeGroup from './useGetAgeGroupsSelectorAndSelectedAgeGroup.js';
import useGetAppointmentTypesForSelectedAgeGroup from './useGetAppointmentTypesForSelectedAgeGroup.js';
import useGetAvailableAgeGroups from './useGetAvailableAgeGroups.js';

const AppointmentTypesContainer = ()=>{
    const { getLabel , getFormattedLabel  } = useIntl();
    const [{ goTo  }, { queryParams  }] = useRoutingContext();
    const [{ setStore , setType  }, { store  }] = useAppointmentContext();
    const [getBookAppointmentStore, getBookAppointmentStoreQuery] = useBookAppointmentStoreById({
        isTryInStoreFlowEnabled: fromArrayToString(queryParams.tryInStoreFlow) === '1' ? true : false
    });
    const { appointmentDisplayType , sortedAppointmentTypes , groupedAppointmentTypes  } = useAppointmentTypes();
    const [activeToolTip, setActiveToolTip] = React.useState(null);
    const labels = React.useMemo(()=>({
            heading: getLabel('appointments.component.appointmentTypes.heading'),
            description: getFormattedLabel('appointments.component.appointmentTypes.description', {
                hideLabelKey: true,
                parseLink: true,
                openLinksInNewTab: true
            }),
            alert: getFormattedLabel('appointments.component.appointmentTypes.noAppointmentsAlert', {
                hideLabelKey: true,
                parseLink: true
            })
        })
    , [
        getLabel,
        getFormattedLabel
    ]);
    const onClick = React.useCallback((type)=>{
        /* istanbul ignore if */ if (!store) {
            // This should never happen, but it's here to satisfy typescript
            return;
        }
        setType(type);
        const typeId = type.id;
        const storeId = `${store.globalStoreId}`;
        const { search , userLocation , linkedAppointmentTypeId  } = queryParams;
        goTo('when', {
            typeId,
            storeId,
            search,
            userLocation,
            linkedAppointmentTypeId
        });
    }, [
        queryParams,
        setType,
        goTo,
        store
    ]);
    const onClickToolTip = React.useCallback((toolTip)=>{
        setActiveToolTip(toolTip);
    }, []);
    const onCloseToolTip = React.useCallback(()=>{
        setActiveToolTip(null);
    }, []);
    const aabWhatPageView = React.useCallback(()=>new Promise((resolve)=>{
            if (!store) {
                return;
            }
            resolve({
                id: 'VirtualPage-View',
                Page_Type: 'Info',
                Page_Section1: 'Exam',
                Page_Section2: 'Question1',
                Store_Search_StoreId: store.code.toString()
            });
        })
    , [
        store
    ]);
    useTrackPageView(aabWhatPageView);
    React.useEffect(()=>{
        /* istanbul ignore if */ if (!store) {
            // This should never happen, but it's here to satisfy typescript
            return;
        }
        const { storeId  } = queryParams;
        const { appointmentTypes  } = store;
        /** Need to check for the appointment types existence to be sure that
     * we are going to send correct store data to the event */ if (storeId === store.globalStoreId && appointmentTypes.length) {
            const dataLayerEvent = bookAppointmentWhatStepScreenEvent({
                store: {
                    code: store.code,
                    globalStoreId: store.globalStoreId,
                    streetName: store.streetName,
                    town: store.town,
                    name: store.name,
                    postalCode: store.postalCode,
                    streetNumber: store.streetNumber,
                    additionalStreetInfo: store.additionalStreetInfo
                }
            });
            DataLayer.pushEvent(dataLayerEvent);
        }
    }, [
        queryParams,
        store
    ]);
    React.useEffect(()=>{
        var ref;
        const { storeId: storeIdFromUrl  } = queryParams;
        const globalStoreId = fromArrayToString(storeIdFromUrl);
        /** If no store in the context but has a storeId in the URL refetch store data */ if (!store) {
            if (globalStoreId) {
                getBookAppointmentStore(globalStoreId);
            }
            return;
        }
        /** Refetch store data with parameters from url
     *  if has different storeId in url and in the context */ if (globalStoreId && globalStoreId !== store.globalStoreId) {
            getBookAppointmentStore(globalStoreId);
            return;
        }
        if (((ref = store.appointmentTypes) === null || ref === void 0 ? void 0 : ref.length) || store.globalStoreId === undefined) {
            return;
        }
        // Only request the store data when no appointmentTypes are found.
        getBookAppointmentStore(store.globalStoreId);
    }, [
        getBookAppointmentStore,
        store
    ]);
    React.useEffect(()=>{
        const { called , data  } = getBookAppointmentStoreQuery;
        if (!called || !data) {
            return;
        }
        setStore(data);
    }, [
        setStore,
        getBookAppointmentStoreQuery
    ]);
    const availableAgeGroups = useGetAvailableAgeGroups(sortedAppointmentTypes);
    const { AgeGroupSelector , selectedAgeGroup  } = useGetAgeGroupsSelectorAndSelectedAgeGroup(availableAgeGroups);
    const appointmentTypes1 = useGetAppointmentTypesForSelectedAgeGroup(selectedAgeGroup, sortedAppointmentTypes);
    const loading = getBookAppointmentStoreQuery.loading;
    return /*#__PURE__*/ React.createElement(AppointmentTypes, {
        labels: labels,
        appointmentDisplayType: appointmentDisplayType,
        AgeGroupSelector: appointmentDisplayType === 'group' ? null : AgeGroupSelector,
        appointmentTypes: appointmentTypes1,
        groupedAppointmentTypes: groupedAppointmentTypes,
        activeToolTip: activeToolTip,
        onClick: onClick,
        onClickToolTip: onClickToolTip,
        onCloseToolTip: onCloseToolTip,
        loading: loading
    });
};

export { AppointmentTypesContainer as default };
//# sourceMappingURL=AppointmentTypesContainer.js.map
